import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormField, form, required } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DtisFormDialogComponent } from '@dtis/angular';
import { TranslocoModule } from '@jsverse/transloco';
import type { OrgaItemBaseModel } from '../../../../models';

export interface CreateOrgItemDialogData {
  parentNode: OrgaItemBaseModel;
  childType: string;
}

export interface CreateOrgItemDialogResult {
  name: string;
  description: string;
}

/**
 * Dialog for creating a new organizational item (Master, Instance, or Configuration)
 */
@Component({
  selector: 'dtis-admin-create-org-item-dialog',
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, TranslocoModule, DtisFormDialogComponent, FormField],
  template: `
    <dtis-form-dialog
      [title]="'add-' + data.childType.toLowerCase()"
      saveLabel="create"
      [saveDisabled]="form.invalid"
      (saved)="onSubmit()"
      (cancelled)="dialogRef.close()"
    >
      <form [formGroup]="form" class="flex">
        <div class="flex w-full flex-col gap-2">
          <p class="text-body-medium text-onSurfaceVariant">
            {{ 'creating-under' | transloco }}: <strong>{{ data.parentNode.name }}</strong>
          </p>

          <div class="flex w-full">
            <mat-form-field class="w-full" appearance="outline">
              <mat-label>{{ 'name' | transloco }}</mat-label>
              <input matInput [formField]="createform$.name" [placeholder]="data.childType + ' ' + 'name' | transloco" />
            </mat-form-field>
          </div>

          <div class="flex w-full">
            <mat-form-field class="w-full" appearance="outline">
              <mat-label>{{ 'description' | transloco }}</mat-label>
              <textarea
                matInput [formField]="createform$.description"
                [placeholder]="'description' | transloco"
                class="h-32"
              ></textarea>
            </mat-form-field>
          </div>
        </div>
      </form>
    </dtis-form-dialog>
  `,
})
export class CreateOrgItemDialogComponent {
  private readonly fb = inject(FormBuilder);
  readonly dialogRef = inject(MatDialogRef<CreateOrgItemDialogComponent>);
  readonly data = inject<CreateOrgItemDialogData>(MAT_DIALOG_DATA);

  form = this.fb.group({
    name: ['', Validators.required],
    description: [''],
  });

  private readonly itemCreateModel = signal<{ name: string; description: string }>({ name: '', description: '' });

  createform$ = form(this.itemCreateModel, (schemaPath) => {
    required(schemaPath.name);
  });

  onSubmit(): void {
    if (this.form.valid) {
      this.dialogRef.close(this.form.value);
    }
  }
}
